import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
  api,
  clearAuth,
  getStoredUser,
  getToken,
  setStoredUser,
  setToken,
  type CurrentUser,
} from './api';

interface LoginResponse {
  token: string;
  user: CurrentUser;
}

interface AuthState {
  user: CurrentUser | null;
  token: string | null;
  loading: boolean;
  /** Üretim girişi (email + bcrypt). */
  login: (email: string, password: string) => Promise<void>;
  /** Geliştirme modu — rol seçimli, sunucu ALLOW_DEV_LOGIN=true ise. */
  devLogin: (email: string, role: CurrentUser['role']) => Promise<void>;
  logout: () => void;
  hasPermission: (perm: string) => boolean;
}

const PERMISSIONS_BY_ROLE: Record<CurrentUser['role'], string[]> = {
  ADMIN: [
    'report.create',
    'report.read',
    'report.approve',
    'report.delete',
    'fair.write',
    'capa.write',
    'audit.write',
    'ygg.write',
  ],
  QA_MANAGER: [
    'report.create',
    'report.read',
    'report.approve',
    'fair.write',
    'capa.write',
    'audit.write',
    'ygg.write',
  ],
  AUDITOR: ['report.read', 'audit.write'],
  READ_ONLY: ['report.read'],
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(() => getStoredUser());
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [loading, setLoading] = useState(false);

  // İlk açılışta token varsa ama user yoksa /v1/auth/me ile doğrula.
  useEffect(() => {
    if (token && !user) {
      setLoading(true);
      api
        .get<CurrentUser>('/v1/auth/me')
        .then((u) => {
          setUser(u);
          setStoredUser(u);
        })
        .catch(() => {
          clearAuth();
          setTokenState(null);
        })
        .finally(() => setLoading(false));
    }
  }, [token, user]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      token,
      loading,
      async login(email, password) {
        setLoading(true);
        try {
          const res = await api.post<LoginResponse>('/v1/auth/login', { email, password });
          setToken(res.token);
          setStoredUser(res.user);
          setTokenState(res.token);
          setUser(res.user);
        } finally {
          setLoading(false);
        }
      },
      async devLogin(email, role) {
        setLoading(true);
        try {
          const res = await api.post<LoginResponse>('/v1/auth/dev-login', { email, role });
          setToken(res.token);
          setStoredUser(res.user);
          setTokenState(res.token);
          setUser(res.user);
        } finally {
          setLoading(false);
        }
      },
      logout() {
        clearAuth();
        setTokenState(null);
        setUser(null);
      },
      hasPermission(perm) {
        if (!user) return false;
        return PERMISSIONS_BY_ROLE[user.role].includes(perm);
      },
    }),
    [user, token, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, token, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="p-8 text-sm text-slate-500">Oturum doğrulanıyor…</div>;
  }
  if (!token || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return <>{children}</>;
}
